<script setup lang="ts">
  /**
   * Required wrapper for BfConsoleNav, and the component's query *container*.
   *
   * Expanded/rail vs. drawer is a container query on this element, not a media query, so the nav
   * also drawers inside a narrow column on a wide screen — the same trade the data table makes.
   * An element is excluded from its own container query, which is why the shell has to be a
   * separate element from the nav. Drop the shell and the nav never becomes a drawer.
   *
   * It also provides the positioning context: in drawer mode the nav is absolute inside the
   * shell rather than fixed to the viewport, so it needs no --bf-header-h-app offset and stays
   * correct in an embedded or scrolled layout.
   */
</script>

<template>
  <div class="console-layout">
    <slot />
  </div>
</template>

<style scoped lang="scss">
  .console-layout {
    position: relative;
    display: flex;
    align-items: stretch;
    container-type: inline-size;
    container-name: bfconsole;
    background: var(--bf-bg-soft);
  }
</style>

<!-- Deliberately unscoped: scoped CSS appends the scope attribute to the last
     compound selector, so a scoped `:root:has(…)` could never match. -->
<style lang="scss">
  /* --toast-viewport-inset-start — where the page content region begins. Read by
     .toast-viewport--application, which centres the client area's global toast
     stack on the content column instead of on the window. The nav's own
     --console-navigation-w / --console-navigation-rail-width are declared on the
     nav element, which is out of scope for a body-level fixed stack, so the hook
     is published at the document root and the two widths are repeated here — the
     three must be changed together.

     Viewport-scoped on purpose, unlike the nav's own expanded -> drawer switch:
     that is a container query on .console-layout, an element cannot query itself,
     and a viewport-fixed stack cannot be told about a container. A console
     embedded in a column narrower than --bf-bp-console sets the hook to 0 where
     it places the shell. */
  :root:has(.console-layout) {
    --toast-viewport-inset-start: 220px; /* --console-navigation-w */
  }

  :root:has(.console-navigation[data-state='rail']) {
    --toast-viewport-inset-start: 64px; /* --console-navigation-rail-width */
  }

  @media (max-width: 860px) {
    /* --bf-bp-console — the nav is an out-of-flow drawer */
    :root:has(.console-layout),
    :root:has(.console-navigation[data-state='rail']) {
      --toast-viewport-inset-start: 0px;
    }
  }
</style>
